import { NotificationTwoTone } from '@ant-design/icons';
import { Modal, Space } from 'antd';
import React from 'react';

import { getUploadFileImageUrl } from '@/helpers/getUploadFilePath';

import { displayDateTimeString } from './helpers';
import styles from './index.module.scss';
import type { EdutainmentDetailValues } from './types';

type EdutainmentDetailModalProps<E extends EdutainmentDetailValues> = {
  open: boolean;
  onCancel: () => void;
  edutainment: E;
  statusSection?: { title: string; content: string }[];
  news?: string;
  modalFooter?: React.ReactNode;
};

const modalBodyStyles: React.CSSProperties = {
  maxHeight: '92vh',
  overflowY: 'auto',
};

export const EdutainmentDetailModal = <E extends EdutainmentDetailValues>({
  open,
  onCancel,
  edutainment,
  statusSection,
  news,
  modalFooter,
}: EdutainmentDetailModalProps<E>) => {
  console.log('edutainment:', edutainment);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      destroyOnClose
      centered
      width={490}
      bodyStyle={modalBodyStyles}
      footer={modalFooter}
    >
      <div className={styles.modalContent}>
        <div className={styles.imageContainer}>
          <img
            src={getUploadFileImageUrl(edutainment.image)}
            alt="育樂活動圖片"
          />
        </div>

        <div className={styles.section}>
          <h1>{edutainment.name}</h1>
        </div>

        {statusSection ? (
          <div className={styles.section}>
            <Space direction="vertical" size="middle">
              <Space>
                <NotificationTwoTone rev={undefined} />
                <h4 className={styles.sectionTitle}>活動狀態：</h4>
              </Space>
              {statusSection.map((item, index) => (
                <div key={index} className={styles.subSection}>
                  <h5>{item.title}</h5>
                  <p>{item.content}</p>
                </div>
              ))}
            </Space>
          </div>
        ) : null}

        {news ? (
          <div className={styles.section}>
            <Space direction="vertical" size="middle">
              <Space>
                <NotificationTwoTone rev={undefined} />
                <h4 className={styles.sectionTitle}>最新消息：</h4>
              </Space>
              <div className={styles.subSection}>
                <h5>最新消息</h5>
                <p className={styles['pre-wrap']}>{news}</p>
              </div>
            </Space>
          </div>
        ) : null}

        <div className={styles.section}>
          <Space direction="vertical" size="middle">
            <Space>
              <NotificationTwoTone rev={undefined} />
              <h4 className={styles.sectionTitle}>活動內容：</h4>
            </Space>
            <div className={styles.subSection}>
              <h5>活動類別</h5>
              <p>{edutainment.category}</p>
            </div>
            <div className={styles.subSection}>
              <h5>活動介紹</h5>
              <p className={styles['pre-wrap']}>{edutainment.description}</p>
            </div>
            <div className={styles.subSection}>
              <h5>費用包含</h5>
              <p className={styles['pre-wrap']}>
                {edutainment.includedPayment}
              </p>
            </div>
          </Space>
        </div>

        <div className={styles.section}>
          <Space direction="vertical" size="middle">
            <Space>
              <NotificationTwoTone rev={undefined} />
              <h4 className={styles.sectionTitle}>活動資訊：</h4>
            </Space>
            <div className={styles.subSection}>
              <h5>講師</h5>
              <p>{edutainment.teacher}</p>
            </div>
            <div className={styles.subSection}>
              <h5>活動起迄日期</h5>
              {/* TODO: 暫時只顯示p tag 文字，到時候要改成可以在需要的情況下，變成顯示link，然後點了可以跳轉到上課課程表的頁面 */}
              <p>
                {displayDateTimeString({
                  startTime: edutainment.program.startTime,
                  endTime: edutainment.program.endTime,
                })}
              </p>
            </div>
            <div className={styles.subSection}>
              <h5>活動上限人數</h5>
              <p>{edutainment.maximumEnrollment}</p>
            </div>
            <div className={styles.subSection}>
              <h5>活動成團人數</h5>
              <p>{edutainment.minimumEnrollment}</p>
            </div>
            <div className={styles.subSection}>
              <h5>報名起迄時間</h5>
              <p>
                {displayDateTimeString({
                  startTime: edutainment.enrollment.startTime,
                  endTime: edutainment.enrollment.endTime,
                  format: 'YYYY/MM/DD HH:mm',
                })}
              </p>
            </div>
          </Space>
        </div>

        <div className={styles.section}>
          <Space direction="vertical" size="middle">
            <Space>
              <NotificationTwoTone rev={undefined} />
              <h4 className={styles.sectionTitle}>活動須知：</h4>
            </Space>
            <div className={styles.subSection}>
              <h5>注意事項</h5>
              <p className={styles['pre-wrap']}>{edutainment.note}</p>
            </div>
            <div className={styles.subSection}>
              <h5>退費規則</h5>
              <p>{edutainment.refundPolicy}</p>
            </div>
          </Space>
        </div>

        <div className={styles.section}>
          <Space
            direction="vertical"
            size="middle"
            className={styles['w-full']}
          >
            <Space>
              <NotificationTwoTone rev={undefined} />
              <h4 className={styles.sectionTitle}>育樂項目：</h4>
            </Space>
            <Space direction="vertical" className={styles['w-full']}>
              <div className={styles.grid}>
                <p className={styles.gridTitle}>方案</p>
                <p className={styles.gridTitle}>價格</p>
                <p>會員</p>
                <p>{edutainment.pricing.member.price}元</p>
                <p>非會員</p>
                <p>{edutainment.pricing.nonMember.price}元</p>
              </div>
            </Space>
          </Space>
        </div>
      </div>
    </Modal>
  );
};
